import { isNameChar, toNameText } from '@privacy-bee/validate/name';

export default function () {
    const truncateText = (content: string, max?: number) => {
        if (!content) return;
        const maxNum = max || 30;
        if (content.length > maxNum) {
            return content.slice(0, maxNum) + '...';
        } else return content;
    };

    /**
     * Formats minutes into days, hours, and minutes format.
     * Example: 3003 => "2d 5h 3m"
     * @param minutes The number of minutes to format
     * @returns The formatted time string
     */
    const formatTimeFromMinutes = (minutes: number | string | null | undefined): string => {
        if (minutes === null || minutes === undefined || minutes === '') return '0m';

        const totalMinutes = typeof minutes === 'string' ? parseInt(minutes) : minutes;

        if (isNaN(totalMinutes) || totalMinutes < 0) return '0m';

        const days = Math.floor(totalMinutes / (24 * 60));
        const remainingMinutesAfterDays = totalMinutes % (24 * 60);
        const hours = Math.floor(remainingMinutesAfterDays / 60);
        const remainingMinutes = remainingMinutesAfterDays % 60;

        const parts = [];

        if (days > 0) {
            parts.push(`${days}d`);
        }

        if (hours > 0) {
            parts.push(`${hours}h`);
        }

        if (remainingMinutes > 0 || parts.length === 0) {
            parts.push(`${remainingMinutes}m`);
        }

        return parts.join(' ');
    };

    /**
     * Strips plus-addressing from an email (e.g. "chris+privacy@gmail.com" -> "chris@gmail.com").
     * Mirrors the normalization the API applies in CheckForPreviousBreachesAction before
     * querying HIBP, so the UI shows the email that was actually scanned.
     */
    const stripEmailPlusAddressing = (email: string): string => {
        if (!email) return email;
        return email.replace(/^([^+]+)\+[^@]+(@.+)$/, '$1$2');
    };

    /** Puts text into the field at the caret and lets v-model see it. */
    function insertNameText(input: HTMLInputElement, text: string): void {
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? start;
        input.value = input.value.slice(0, start) + text + input.value.slice(end);
        const caret = start + text.length;
        input.setSelectionRange(caret, caret);
        // A programmatic value change fires no event of its own.
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * Single event handler that holds a field to the characters a name is made of.
     * Use as: @keydown="letterOnlyInputHandler" @paste="letterOnlyInputHandler" @input="letterOnlyInputHandler"
     * @param event KeyboardEvent | ClipboardEvent | InputEvent
     */
    function letterOnlyInputHandler(event: Event) {
        // Handle paste: insert only the name characters the clipboard carries
        if (event.type === 'paste') {
            const e = event as ClipboardEvent;
            e.preventDefault();

            const pastedText = e.clipboardData?.getData('text') || '';

            insertNameText(event.target as HTMLInputElement, toNameText(pastedText));

            return;
        }

        // Handle keydown: block keys that put a non-name character in the field
        if (event.type === 'keydown') {
            const e = event as KeyboardEvent;
            const editingKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Delete',
                'Home',
                'End',
                'Shift',
                'Control',
                'Meta',
                'Alt',
            ];
            if (editingKeys.includes(e.key)) return;
            // Allow dead keys (for diacritic composition on macOS/Linux)
            if (e.key === 'Dead') return;
            if (isNameChar(e.key)) {
                const folded = toNameText(e.key);
                // A key the fold leaves alone takes the browser's own insert, which
                // keeps undo history, IME composition and autofill intact. Only a key
                // the fold rewrites — the smart apostrophe, a non-breaking space, an
                // en dash — is intercepted, so a typed name and a pasted one reach the
                // field as the same string.
                if (folded === e.key) return;
                e.preventDefault();
                insertNameText(e.target as HTMLInputElement, folded);
                return;
            }
            // Block everything else (numbers, special chars, etc.)
            e.preventDefault();
            return;
        }

        // Handle input: strip non-name characters from a value the other two never saw
        // (autofill, drag and drop)
        if (event.type === 'input') {
            const input = event.target as HTMLInputElement;
            const filtered = toNameText(input.value);
            if (input.value !== filtered) {
                input.value = filtered;
            }
        }
    }

    return {
        truncateText,
        formatTimeFromMinutes,
        stripEmailPlusAddressing,
        letterOnlyInputHandler,
    };
}
